<template>
  <Teleport to="body">
    <client-only>
      <Transition name="fade4">
        <!-- Figma の ModalBackground: 全画面 bg-black/20 + 2px blur。PC のみ表示、SP は本体がフルスクリーン -->
        <div
          v-if="props.shouldShowModal"
          aria-label="Keyword search"
          class="fixed inset-0 z-global-modal flex justify-center md:items-start md:bg-black/20 md:pb-12 md:pt-[160px] md:backdrop-blur-[2px]"
          role="dialog"
          @click.self="props.onClose"
        >
          <!-- モーダル本体: Figma 通り bg-white/90 (#ffffffe5) + backdrop-blur-[12px]。
               PC: w-640 / min-h-400 / max-h は viewport から pt-160 と pb-48 を差し引いた高さ。height は内容追従。
               SP: 全画面 -->
          <div
            class="relative flex size-full flex-col overflow-hidden bg-white/90 backdrop-blur-[12px] md:h-auto md:max-h-[calc(100vh-160px-48px)] md:min-h-[400px] md:w-full md:max-w-[640px] md:rounded-sm md:shadow-xl"
          >
            <AisInstantSearch v-if="searchClient && indexName" class="flex min-h-0 flex-1 flex-col" :index-name="indexName" :search-client="searchClient">
              <AisConfigure :click-analytics.camel="true" :hits-per-page.camel="MAX_HITS_PER_PAGE" :query="query" />

              <SearchBox :model-value="query" :on-arrow-down="focusFirstResult" :on-close="props.onClose" :on-update-model-value="updateQuery" />

              <!-- scroll コンテナ。bg は持たない（モーダル本体の半透明白＋blur が透ける）。
                   セクション間の gap は各ステートのコンポーネント内で制御する -->
              <ScrollArea ref="scrollAreaRef" class="min-h-0 flex-1 overscroll-contain" @touchstart.passive="blurSearchInput">
                <SearchInitialState
                  v-if="!query"
                  :destinations="destinationFeaturedList"
                  :on-close="props.onClose"
                  :on-open-dates-filter="handleOpenDatesFilter"
                  :on-view-more-destinations="handleViewMoreDestinations"
                  :on-view-more-preferences="handleViewMorePreferences"
                  :on-view-more-themes="handleViewMoreThemes"
                  :preferences="preferenceTags"
                  :themes="themeFeaturedList"
                />

                <AisStateResults v-else>
                  <template #default="{ results, state }">
                    <SearchResultsTracker :loading="isLoading(results, state)" :query="query" :result-count="results?.nbHits ?? 0" />
                    <SearchSkeleton v-if="isLoading(results, state)" />
                    <template v-else>
                      <SearchResults
                        v-if="results && results.nbHits > 0"
                        :cms-destinations="destinationFeaturedList"
                        :cms-themes="themeFeaturedList"
                        :hits="results.hits as AlgoliaHit[]"
                        :on-hit-click="handleHitClick"
                        :tags="tags ?? []"
                      />
                      <template v-else>
                        <SearchEmpty />
                        <!-- Figma の 0 件状態: Empty 文言の下に初期状態（コンパクト版: 見出しにアイコン）を表示 -->
                        <SearchInitialState
                          :destinations="destinationFeaturedList"
                          :on-close="props.onClose"
                          :on-view-more-destinations="handleViewMoreDestinations"
                          :on-view-more-preferences="handleViewMorePreferences"
                          :on-view-more-themes="handleViewMoreThemes"
                          :preferences="preferenceTags"
                          :themes="themeFeaturedList"
                          compact
                        />
                      </template>
                    </template>
                  </template>
                </AisStateResults>
              </ScrollArea>
            </AisInstantSearch>
          </div>
        </div>
      </Transition>
    </client-only>
  </Teleport>
</template>

<script setup lang="ts">
import { AisConfigure, AisInstantSearch, AisStateResults } from 'vue-instantsearch/vue3/es';

import { ScrollArea } from '@/components/lib/shadcn-vue/scroll-area';
import { useAsyncWabunkaTags } from '@/composables/models/wabunka/useAsyncWabunkaTags';
import { useCurrentTabIndex } from '@/composables/ui/useCurrentTabIndex';
import { useGlobalModal } from '@/composables/ui/useGlobalModal';
import { useFeaturedListQuery } from '@/wabunka-src/common/features/Featured/composables/query/useGetFeaturedListQuery';
import { getBackgroundImageUrl } from '@/wabunka-src/common/features/Featured/models/minimunFeatured/minimumFeatured';
import { normalizeSearchKeyword } from '@/wabunka-src/common/utils/googleAnalytics/common/searchKeyword';
import { saveSearchTouchKeyword } from '@/wabunka-src/common/utils/googleAnalytics/common/searchPurchaseKeyword';
import { sendGAWabunkaSearchInputStartedEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/searchInputStarted';
import { sendGAWabunkaSearchResultClickEvent, type SearchResultType } from '@/wabunka-src/common/utils/googleAnalytics/events/searchResultClick';
import { SEARCH_TAB_INDEX } from '@/wabunka-src/constants/navigation';
import SearchBox from '@/wabunka-src/features/Search/components/SearchBox/SearchBox.vue';
import SearchEmpty from '@/wabunka-src/features/Search/components/SearchEmpty/SearchEmpty.vue';
import SearchInitialState from '@/wabunka-src/features/Search/components/SearchInitialState/SearchInitialState.vue';
import SearchResults from '@/wabunka-src/features/Search/components/SearchResults/SearchResults.vue';
import SearchResultsTracker from '@/wabunka-src/features/Search/components/SearchResults/SearchResultsTracker.vue';
import SearchSkeleton from '@/wabunka-src/features/Search/components/SearchSkeleton/SearchSkeleton.vue';
import { useAlgoliaClickedEvent } from '@/wabunka-src/features/Search/composables/general/useAlgoliaClickedEvent';
import { useAlgoliaSearchClient } from '@/wabunka-src/features/Search/composables/useAlgoliaSearchClient';
import { DESTINATION_THEME_CATEGORY_LABEL, isExperienceHit, isFeatureHit, normalizeWebPath, type AlgoliaHit } from '@/wabunka-src/features/Search/models/algoliaHit';

interface Props {
  shouldShowModal: boolean;
  onClose: () => void;
}

const props = defineProps<Props>();

const MAX_HITS_PER_PAGE = 60;

const { searchClient, indexName } = useAlgoliaSearchClient();

const query = ref('');
// モーダル1開封につき search_input_started を1回だけ発火するためのガード（開封時にリセット）
const hasFiredInputStarted = ref(false);
const updateQuery = (next: string) => {
  query.value = next;
  if (!hasFiredInputStarted.value && next.trim().length > 0) {
    hasFiredInputStarted.value = true;
    sendGAWabunkaSearchInputStartedEvent();
  }
};

// Destinations / Themes は「画像付きの特集ページ」を一覧したいため microCMS (en-featured) から取得する。
// Preferences は特集ページが無いので tags API から取得する。Algolia は検索結果（query > 0）にのみ使う。
// useFeaturedListQuery と useAsyncWabunkaTags でエラー戦略を揃える（どちらも throw して Promise.all 全体を reject させる）。
const [{ destinationList, themeList }, { tags }] = await Promise.all([useFeaturedListQuery({ types: ['destination', 'theme'] }), useAsyncWabunkaTags({ throwOnError: true })]);
const destinationFeaturedList = computed(() => destinationList.value.filter((f) => getBackgroundImageUrl(f).length > 0));
const themeFeaturedList = computed(() => themeList.value.filter((f) => getBackgroundImageUrl(f).length > 0));
// 初期状態の Preferences は Instant booking / Families with kids の 2 つだけを表示する
const INITIAL_PREFERENCE_NAMES = ['Instant booking', 'Families with kids'];
const preferenceTags = computed(() =>
  (tags.value ?? [])
    .filter((t) => t.tagGroupType === 'preference' && INITIAL_PREFERENCE_NAMES.some((name) => name.toLowerCase() === t.name.toLowerCase()))
    .sort((a, b) => INITIAL_PREFERENCE_NAMES.findIndex((n) => n.toLowerCase() === a.name.toLowerCase()) - INITIAL_PREFERENCE_NAMES.findIndex((n) => n.toLowerCase() === b.name.toLowerCase())),
);

watch(
  () => props.shouldShowModal,
  (isOpen) => {
    if (isOpen) {
      hasFiredInputStarted.value = false;
    } else {
      query.value = '';
    }
  },
);

// 入力欄以外（結果一覧）への接触・スクロール開始でキーボードを閉じる。
// スクロールは touchstart で始まるため、結果一覧側の touchstart で入力欄を blur すれば
// 「スクロール開始」と「入力欄以外のタップ」の両方をカバーできる。
const blurSearchInput = () => {
  const el = document.activeElement;
  if (el instanceof HTMLElement && el.tagName === 'INPUT') el.blur();
};

const isLoading = (results: { __isArtificial?: boolean; query?: string } | undefined, state: { query?: string } | undefined): boolean => {
  if (!results) return true;
  if (results.__isArtificial) return true;
  if (state?.query !== undefined && results.query !== state.query) return true;
  return false;
};

// SearchBox の ArrowDown 押下時に、scroll コンテナ内の最初のフォーカス可能要素（a/button）へフォーカスを移す。
// 結果リストの先頭を Enter で開けるようにするためのキーボード操作補助。
const scrollAreaRef = ref<{ $el?: HTMLElement } | HTMLElement | null>(null);
const focusFirstResult = () => {
  nextTick(() => {
    const root = (scrollAreaRef.value as { $el?: HTMLElement } | null)?.$el ?? (scrollAreaRef.value as HTMLElement | null);
    if (!root) return;
    const first = root.querySelector<HTMLElement>('a, button:not([disabled])');
    first?.focus();
  });
};

// 各セクションの View More: フィルターモーダル (SearchExperiences) を対応タブで開く
const { open: openSearchExperiencesModal } = useGlobalModal('searchExperiencesModal');
const { updateTabIndex } = useCurrentTabIndex('searchExperiencesModalTabIndex');
const openSearchExperiencesAt = (tabIndex: number) => {
  props.onClose();
  updateTabIndex(tabIndex);
  openSearchExperiencesModal();
};
const handleViewMoreDestinations = () => openSearchExperiencesAt(SEARCH_TAB_INDEX.DESTINATIONS);
const handleViewMoreThemes = () => openSearchExperiencesAt(SEARCH_TAB_INDEX.THEMES);
const handleViewMorePreferences = () => openSearchExperiencesAt(SEARCH_TAB_INDEX.PREFERENCES);
// Dates セクション (preset チップ含む) は日程タブのフィルターモーダルへ
const handleOpenDatesFilter = () => openSearchExperiencesAt(SEARCH_TAB_INDEX.DATES);

// クリックした hit から GA・帰属用の値（種別 / 遷移先キー / タググループ）を取り出す
const buildClickAttribution = (hit: AlgoliaHit): { resultType: SearchResultType; clickedSlug: string; tagGroupType?: string } => {
  if (isExperienceHit(hit)) return { resultType: 'experience', clickedSlug: hit.experience.slug };
  if (isFeatureHit(hit)) {
    // destination-theme は feature に相乗りしているため categoryLabel で見分け、専用の resultType を送る。
    // clicked_slug は遷移先（buildDestinationThemePath）と同じ正規化規則で先頭/末尾スラッシュを除去し、
    // 末尾スラッシュ有無による GA 集計の分断を防ぐ（他 resultType の clickedSlug も先頭スラッシュ無しに揃える）。
    if (hit.feature.categoryLabel === DESTINATION_THEME_CATEGORY_LABEL) return { resultType: 'destinationTheme', clickedSlug: normalizeWebPath(hit.feature.webPath) || hit.feature.slug };
    return { resultType: 'feature', clickedSlug: hit.feature.slug };
  }
  return { resultType: 'tag', clickedSlug: hit.tag.webPath, tagGroupType: hit.tag.tagGroupType };
};

const { sendClickedAfterSearch } = useAlgoliaClickedEvent();
const handleHitClick = (hit: AlgoliaHit) => {
  if (hit.__queryID && indexName) {
    sendClickedAfterSearch(hit);
  }

  const keyword = normalizeSearchKeyword(query.value);
  const { resultType, clickedSlug, tagGroupType } = buildClickAttribution(hit);
  sendGAWabunkaSearchResultClickEvent({ keyword, resultType, clickedSlug, tagGroupType, position: hit.__position });
  // ③「売れるクエリ」用: 後続の purchase に search_keyword を引き継ぐため、検索語のみ last-touch で保存する
  saveSearchTouchKeyword(keyword);

  props.onClose();
};
</script>
